import { FormEvent, useEffect, useMemo, useState } from 'react';
import {
    Alert,
    Autocomplete,
    Box,
    Button,
    CircularProgress,
    MenuItem,
    Paper,
    Stack,
    TextField,
    Typography
} from '@mui/material';
import { useSettings } from 'hooks';
import type { Settings } from 'features/general/types';
import { getPaymentMethods } from 'features/paymentMethods/api';
import { getAuthToken } from 'features/auth/helpers';
import { Filters } from 'consts/paymentMethods';
import { BooleanSwitchValue } from 'consts/general';
import { RequestQueryMapper } from 'util/request-query-mapper';
import useVisiblePaymentMethods from 'ui/organizms/PaymentMethods/VisiblePaymentMethods/useVisiblePaymentMethods';
import { paymentMethodCatalog } from './content';

type TransactionForm = {
    tag: string;
    currency: string;
    amount: string;
    countrycode: string;
    trackingmembercode: string;
    merchantredirecturl: string;
    notificationurl: string;
    source_url: string;
    source_ip: string;
    first_name: string;
    last_name: string;
    email: string;
    date_of_birth: string;
    bank: string;
    phone: string;
    phone_verified: string;
    address: string;
    zip: string;
    city: string;
    country: string;
    nationality: string;
    specin: string;
};

const initialForm: TransactionForm = {
    tag: '', currency: 'EUR', amount: '1.00', countrycode: '',
    trackingmembercode: `docs-${Date.now()}`,
    merchantredirecturl: 'https://testapi.payments-gateway.co/notifications',
    notificationurl: 'https://testapi.payments-gateway.co/notifications',
    source_url: 'https://example.com', source_ip: '', first_name: '', last_name: '',
    email: '', date_of_birth: '', bank: '', phone: '', phone_verified: '1', address: '', zip: '', city: '',
    country: '', nationality: '',
    specin: ''
};

const requiredFields: ReadonlyArray<keyof TransactionForm> = [
    'tag', 'currency', 'amount', 'countrycode', 'trackingmembercode', 'merchantredirecturl',
    'notificationurl', 'first_name', 'last_name', 'email'
];

const hiddenFields: ReadonlyArray<keyof TransactionForm> = [
    'source_url',
    'source_ip',
    'phone_verified'
];

const fieldLabels: Record<keyof TransactionForm, string> = {
    tag: 'Payment method tag', currency: 'Currency', amount: 'Amount', countrycode: 'Transaction country',
    trackingmembercode: 'Tracking member code', merchantredirecturl: 'Merchant redirect URL',
    notificationurl: 'Notification URL', source_url: 'Source URL', source_ip: 'Consumer IP address',
    first_name: 'First name', last_name: 'Last name', email: 'Email',
    date_of_birth: 'Date of birth', bank: 'Bank ID', phone: 'Phone',
    phone_verified: 'Phone verified (1 or 0)', address: 'Address', zip: 'ZIP / postal code',
    city: 'City', country: 'Customer country', nationality: 'Nationality', specin: 'Specin'
};

type SelectedMethodDetails = {
    readonly tag: string;
    readonly countries: readonly string[];
    readonly currencies: readonly string[];
};

type PaymentMethodReference = {
    readonly tag?: string | number | null;
};

type VisiblePaymentMethodReference = PaymentMethodReference & {
    readonly isVisible?: boolean | number | string | null;
};

type GatewayPaymentMethod = {
    readonly tag?: string | number | null;
    readonly countriesAvailable?: string | readonly string[] | null;
    readonly currenciesAvailable?: string | readonly string[] | null;
};

const normalizeTag = (tag: string | number | null | undefined) => String(tag ?? '').trim().toLowerCase();

const splitOptions = (value: string | readonly string[] | null | undefined) => Array.from(new Set(
    (Array.isArray(value) ? value : String(value ?? '').split(','))
        .map(option => option.trim())
        .filter(Boolean)
));

export const normalizeCurrency = (value: string) =>
    value.match(/\b[A-Z]{3}\b/i)?.[0].toUpperCase() ?? value.trim();

const splitCurrencyOptions = (value: string | readonly string[] | null | undefined) => Array.from(new Set(
    (Array.isArray(value) ? value : [String(value ?? '')])
        .flatMap(option => option.match(/\b[A-Z]{3}\b/gi) ?? [])
        .map(normalizeCurrency)
        .filter(Boolean)
));

const isVisibleMethod = (value?: number | string | boolean | null) =>
    value === true || value === 1 || value === '1';

export const composePlaygroundPaymentMethods = (
    enabledMethods: readonly PaymentMethodReference[],
    visibleMethods: readonly VisiblePaymentMethodReference[],
    gatewayMethods: readonly GatewayPaymentMethod[]
): SelectedMethodDetails[] => {
    const enabledTags = new Set(enabledMethods.map(method => normalizeTag(method.tag)).filter(Boolean));
    const visibleTags = new Set(visibleMethods
        .filter(method => isVisibleMethod(method.isVisible))
        .map(method => normalizeTag(method.tag))
        .filter(Boolean));
    const groupedMethods = new Map<string, { countries: Set<string>; currencies: Set<string> }>(
        Array.from(enabledTags)
            .filter(tag => visibleTags.has(tag))
            .map(tag => [tag, { countries: new Set<string>(), currencies: new Set<string>() }])
    );

    gatewayMethods.forEach(method => {
        const tag = normalizeTag(method.tag);
        if (!tag || !groupedMethods.has(tag)) return;

        const group = groupedMethods.get(tag)!;
        splitOptions(method.countriesAvailable).forEach(country => group.countries.add(country));
        splitCurrencyOptions(method.currenciesAvailable)
            .forEach(currency => group.currencies.add(currency));
    });

    return Array.from(groupedMethods.entries())
        .map(([tag, details]) => ({
            tag,
            countries: Array.from(details.countries).sort(),
            currencies: Array.from(details.currencies).sort()
        }))
        .sort((first, second) => first.tag.localeCompare(second.tag));
};

const getResources = (payload: any): PaymentMethodReference[] => {
    const data = payload?.data?.data ?? payload?.data ?? payload;
    return Array.isArray(data) ? data : [];
};

const catalogGatewayMethods: GatewayPaymentMethod[] = paymentMethodCatalog.map(method => ({
    tag: method.tag,
    countriesAvailable: method.countries,
    currenciesAvailable: method.currencies
}));

const sandboxTransactionUrl = `${(process.env.REACT_APP_BACKEND_ORIGIN_URL ?? '')
    .replace(/\/$/, '')}/sandbox-demo/v1/transaction/submit`;

const findRedirectUrl = (value: unknown): string | null => {
    if (!value || typeof value !== 'object') return null;

    for (const [key, nestedValue] of Object.entries(value)) {
        if (
            key.replace(/[^a-z]/gi, '').toLowerCase() === 'redirecturl' &&
            typeof nestedValue === 'string' &&
            /^https?:\/\//i.test(nestedValue)
        ) {
            return nestedValue;
        }

        const nestedRedirectUrl = findRedirectUrl(nestedValue);
        if (nestedRedirectUrl) return nestedRedirectUrl;
    }

    return null;
};

const TransactionPlayground = () => {
    const { getSettings } = useSettings<Settings>();
    const settings = getSettings();
    const visiblePaymentMethods = useVisiblePaymentMethods();
    const [form, setForm] = useState(initialForm);
    const [isSubmitting, setSubmitting] = useState(false);
    const [httpStatus, setHttpStatus] = useState<number | null>(null);
    const [response, setResponse] = useState<unknown>(null);
    const [error, setError] = useState<string | null>(null);
    const [enabledMethods, setEnabledMethods] = useState<PaymentMethodReference[]>([]);
    const [gatewayMethods, setGatewayMethods] = useState<GatewayPaymentMethod[]>([]);
    const [methodsError, setMethodsError] = useState<string | null>(null);
    const [areMethodDetailsLoading, setMethodDetailsLoading] = useState(true);
    const hasCredentials = Boolean(settings?.apiId && settings?.apiGuid);
    const jwtToken = getAuthToken();

    useEffect(() => {
        let isMounted = true;
        if (!hasCredentials) {
            setMethodDetailsLoading(false);
            return () => { isMounted = false; };
        }

        setMethodDetailsLoading(true);
        setMethodsError(null);

        const enabledQuery = RequestQueryMapper.from()
            .containsIn(Filters.isEnabled, `${BooleanSwitchValue.On}`)
            .contains('perPage', '500')
            .toString();

        Promise.all([
            getPaymentMethods(enabledQuery),
            fetch('/payment-gateway/v1/payment-methods', {
                headers: {
                    Accept: 'application/json',
                    'auth-id': settings.apiId,
                    'auth-secret': settings.apiGuid,
                    ...(jwtToken && { Authorization: `Bearer ${jwtToken}` })
                }
            })
        ])
            .then(async ([enabledResponse, gatewayResponse]) => {
                if (!enabledResponse.ok) throw new Error(`Enabled methods HTTP ${enabledResponse.status}`);

                const enabledPayload = await enabledResponse.json();
                const gatewayContentType = gatewayResponse.headers.get('content-type') ?? '';
                const gatewayPayload = gatewayResponse.ok && gatewayContentType.includes('application/json')
                    ? await gatewayResponse.json()
                    : catalogGatewayMethods;
                if (enabledPayload?.success === false) {
                    throw new Error(enabledPayload.message || `API error ${enabledPayload.code ?? 'unknown'}`);
                }

                if (isMounted) {
                    setEnabledMethods(getResources(enabledPayload));
                    setGatewayMethods(Array.isArray(gatewayPayload) ? gatewayPayload : (gatewayPayload?.data ?? []));
                }
            })
            .catch(requestError => {
                if (isMounted) {
                    const details = requestError instanceof Error ? requestError.message : 'Unknown error';
                    setMethodsError(`Enabled payment methods could not be loaded: ${details}.`);
                }
            })
            .finally(() => {
                if (isMounted) setMethodDetailsLoading(false);
            });

        return () => { isMounted = false; };
    }, [hasCredentials, jwtToken, settings?.apiGuid, settings?.apiId]);

    const assignedMethodOptions = useMemo(() => composePlaygroundPaymentMethods(
        enabledMethods,
        visiblePaymentMethods.getPaymentMethods(),
        gatewayMethods
    ), [enabledMethods, gatewayMethods, visiblePaymentMethods]);

    const selectedMethod: SelectedMethodDetails | null = assignedMethodOptions.find(
        method => method.tag.toLowerCase() === form.tag.toLowerCase()
    ) ?? null;

    const requestPreview = useMemo(() => ({
        api_id: settings?.apiId ? '••••••••••••' : 'Loaded from merchant profile',
        api_guid: settings?.apiGuid ? '••••••••••••' : 'Loaded from merchant profile',
        channel: 'testchannel',
        tag: form.tag, currency: normalizeCurrency(form.currency), amount: form.amount, countrycode: form.countrycode,
        trackingmembercode: form.trackingmembercode, merchantredirecturl: form.merchantredirecturl,
        notificationurl: form.notificationurl,
        ...(form.specin && { specin: form.specin }),
        customer: {
            first_name: form.first_name, last_name: form.last_name, email: form.email,
            ...(form.date_of_birth && { date_of_birth: form.date_of_birth }),
            ...(form.phone && { phone: form.phone }),
            ...(form.address && { address: form.address }), ...(form.zip && { zip: form.zip }),
            ...(form.city && { city: form.city }), ...(form.country && { country: form.country }),
            ...(form.nationality && { nationality: form.nationality })
        },
        ...(form.bank && { bank_data: { bank: Number(form.bank) } })
    }), [form, settings?.apiGuid, settings?.apiId]);

    const setField = (field: keyof TransactionForm, value: string) =>
        setForm(current => ({ ...current, [field]: value }));

    const setPaymentMethod = (tag: string) => {
        setForm(current => ({
            ...current,
            tag,
            countrycode: '',
            currency: ''
        }));
    };

    const onSubmit = async (event: FormEvent<HTMLFormElement>) => {
        event.preventDefault();
        setError(null); setResponse(null); setHttpStatus(null);
        if (!hasCredentials) {
            setError('API credentials could not be loaded from the merchant profile.');
            return;
        }

        const body = {
            channel: 'testchannel',
            tag: form.tag, currency: normalizeCurrency(form.currency), amount: form.amount, countrycode: form.countrycode,
            trackingmembercode: form.trackingmembercode, merchantredirecturl: form.merchantredirecturl,
            notificationurl: form.notificationurl,
            ...(form.specin && { specin: form.specin }),
            customer: {
                first_name: form.first_name,
                last_name: form.last_name,
                email: form.email,
                ...(form.date_of_birth && { date_of_birth: form.date_of_birth }),
                ...(form.phone && { phone: form.phone }),
                ...(form.address && { address: form.address }),
                ...(form.zip && { zip: form.zip }),
                ...(form.city && { city: form.city }),
                ...(form.country && { country: form.country }),
                ...(form.nationality && { nationality: form.nationality })
            },
            ...(form.bank && { bank_data: { bank: Number(form.bank) } })
        };

        const redirectTab = window.open('about:blank', '_blank');
        if (redirectTab) {
            redirectTab.opener = null;
            redirectTab.document.title = 'Preparing secure payment…';
            redirectTab.document.body.textContent = 'Preparing secure payment…';
        }

        try {
            setSubmitting(true);
            const result = await fetch(sandboxTransactionUrl, {
                method: 'POST',
                headers: {
                    Accept: 'application/json',
                    'Content-Type': 'application/json',
                    'auth-id': settings.apiId,
                    'auth-secret': settings.apiGuid,
                    ...(jwtToken && { Authorization: `Bearer ${jwtToken}` })
                },
                body: JSON.stringify(body)
            });
            setHttpStatus(result.status);
            const text = await result.text();
            let parsedResponse: unknown = null;
            try { parsedResponse = text ? JSON.parse(text) : null; } catch { parsedResponse = text; }
            setResponse(parsedResponse);

            const redirectUrl = findRedirectUrl(parsedResponse);
            if (result.ok && redirectUrl) {
                if (redirectTab) {
                    redirectTab.location.replace(redirectUrl);
                } else {
                    setError('The transaction was created, but the browser blocked the payment tab. Allow pop-ups and try again.');
                }
            } else {
                redirectTab?.close();
            }

            if (!result.ok) setError(`The sandbox returned HTTP ${result.status}. Review the response for details.`);
        } catch (requestError) {
            redirectTab?.close();
            setError(requestError instanceof Error ? requestError.message : 'The request could not be completed.');
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <Box component='section' id='transaction-playground' sx={{ scrollMarginTop: 104, py: 4 }}>
            <Typography variant='h4' gutterBottom>Try creating a transaction</Typography>
            <Typography color='text.secondary' paragraph>
                Send a test transaction without copying your API credentials. This playground always uses
                the sandbox <Box component='code'>testchannel</Box>.
            </Typography>
            <Alert severity={hasCredentials ? 'success' : 'warning'} sx={{ mb: 3 }}>
                {hasCredentials
                    ? 'API ID and API GUID were loaded from the signed-in merchant profile. The API GUID is never displayed.'
                    : 'API credentials are not available for this merchant profile.'}
            </Alert>
            {methodsError && <Alert severity='warning' sx={{ mb: 3 }}>{methodsError}</Alert>}

            <Box component='form' onSubmit={onSubmit}>
                <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: 'repeat(2, minmax(0, 1fr))' }, gap: 2 }}>
                    {(Object.keys(form) as Array<keyof TransactionForm>)
                        .filter(field => !hiddenFields.includes(field))
                        .map(field => field === 'tag' ? (
                        <TextField key={field} select label={fieldLabels[field]} value={form[field]} required
                            disabled={visiblePaymentMethods.isLoading || !assignedMethodOptions.length}
                            helperText={visiblePaymentMethods.isLoading
                                ? 'Loading active payment methods…'
                                : (!assignedMethodOptions.length ? 'No active payment methods are displayed for this merchant.' : undefined)}
                            onChange={event => setPaymentMethod(event.target.value)} fullWidth>
                            {assignedMethodOptions.map(method => (
                                <MenuItem key={method.tag} value={method.tag}>
                                    {method.tag}
                                </MenuItem>
                            ))}
                        </TextField>
                    ) : field === 'countrycode' ? (
                        <Autocomplete key={field}
                            options={selectedMethod?.countries ?? []}
                            value={form[field] || null}
                            disabled={!form.tag || areMethodDetailsLoading || !selectedMethod?.countries.length}
                            onChange={(_, value) => setField(field, value ?? '')}
                            autoHighlight
                            renderInput={params => (
                                <TextField {...params} label={fieldLabels[field]} required fullWidth
                                    helperText={areMethodDetailsLoading ? 'Loading supported countries…' : form.tag && !selectedMethod?.countries.length
                                        ? 'No countries are available for this tag.'
                                        : 'Type to search supported countries.'}
                                />
                            )}
                        />
                    ) : field === 'currency' ? (
                        <Autocomplete key={field}
                            options={selectedMethod?.currencies ?? []}
                            value={form[field] || null}
                            disabled={!form.tag || areMethodDetailsLoading || !selectedMethod?.currencies.length}
                            onChange={(_, value) => setField(field, value ?? '')}
                            autoHighlight
                            renderInput={params => (
                                <TextField {...params} label={fieldLabels[field]} required fullWidth
                                    helperText={areMethodDetailsLoading ? 'Loading supported currencies…' : form.tag && !selectedMethod?.currencies.length
                                        ? 'No currencies are available for this tag.'
                                        : 'Type to search supported currencies.'}
                                />
                            )}
                        />
                    ) : field === 'specin' ? (
                        <TextField key={field} label={fieldLabels[field]} value={form[field]}
                            helperText='Optional. The value is sent as plain text.'
                            onChange={event => setField(field, event.target.value)} fullWidth />
                    ) : field === 'date_of_birth' ? (
                        <TextField key={field} label={fieldLabels[field]} value={form[field]}
                            type='date' InputLabelProps={{ shrink: true }}
                            helperText='Optional. Sent as customer.date_of_birth in YYYY-MM-DD format.'
                            onChange={event => setField(field, event.target.value)} fullWidth />
                    ) : field === 'bank' ? (
                        <TextField key={field} label={fieldLabels[field]} value={form[field]}
                            type='number' inputProps={{ min: 1, step: 1 }}
                            helperText='Optional. Sent as the numeric bank_data.bank value.'
                            onChange={event => setField(field, event.target.value)} fullWidth />
                    ) : (
                        <TextField key={field} label={fieldLabels[field]} value={form[field]}
                            required={requiredFields.includes(field)} type={field === 'email' ? 'email' : 'text'}
                            onChange={event => setField(field, event.target.value)} fullWidth />
                    ))}
                </Box>
                <Stack direction='row' spacing={2} alignItems='center' sx={{ mt: 3 }}>
                    <Button type='submit' variant='contained' disabled={!hasCredentials || isSubmitting}>
                        {isSubmitting ? 'Sending…' : 'Create test transaction'}
                    </Button>
                    {isSubmitting && <CircularProgress size={22} />}
                </Stack>
            </Box>

            <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', lg: 'repeat(2, minmax(0, 1fr))' }, gap: 2, mt: 4 }}>
                <Paper variant='outlined' sx={{ overflow: 'hidden' }}>
                    <Typography variant='subtitle2' sx={{ px: 2, py: 1.5, bgcolor: 'action.hover' }}>Request preview</Typography>
                    <Box component='pre' sx={{ m: 0, p: 2, overflow: 'auto', fontSize: 12 }}>{JSON.stringify(requestPreview, null, 2)}</Box>
                </Paper>
                <Paper variant='outlined' sx={{ overflow: 'hidden' }}>
                    <Typography variant='subtitle2' sx={{ px: 2, py: 1.5, bgcolor: 'action.hover' }}>
                        Response {httpStatus !== null ? `— HTTP ${httpStatus}` : ''}
                    </Typography>
                    <Box component='pre' sx={{ m: 0, p: 2, overflow: 'auto', fontSize: 12, minHeight: 160 }}>
                        {response === null ? 'Submit the form to see the sandbox response.' : JSON.stringify(response, null, 2)}
                    </Box>
                </Paper>
            </Box>
            {error && <Alert severity='error' sx={{ mt: 2 }}>{error}</Alert>}
        </Box>
    );
};

export default TransactionPlayground;
